import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '../../../lib/queryKeys'
import { createPickupOrder, getPickupOrders, updatePickupOrder } from '../pickupOrdersService'
import { CreatePickupOrderPayload, PickupOrder, PickupOrderStatus } from '../types/PickupOrder'

interface Props {
  status?: string
}

export const usePickupOrders = ({ status }: Props = {}) => {
  const queryKey = [QUERY_KEYS.PICKUP_ORDERS, status].filter(Boolean) as string[]

  return useQuery({
    queryKey,
    queryFn: () => getPickupOrders({ status }),
  })
}

export const useCreatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePickupOrderPayload) => createPickupOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS] })
    },
  })
}

export const useUpdatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PickupOrderStatus }) => updatePickupOrder(id, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS] })
      const previousOrders = queryClient.getQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS])
      queryClient.setQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS], old =>
        old?.map(order => (order.id === id ? { ...order, status } : order))
      )
      return { previousOrders }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData([QUERY_KEYS.PICKUP_ORDERS], context.previousOrders)
      }
    },
  })
}
